import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Props {
  clientId: string;
  recipientEmail: string | null;
  onComplete: () => void;
}

type ActionChoice = "Call us" | "Fill a lead form" | "Buy a product" | "Book a meeting";

interface Power5 {
  priority: string;        // Q1 — top 3 things to rank for
  audience: string;        // Q2 — ideal customer + location
  action: ActionChoice[];  // Q3 — visitor actions (multi-select)
  edge: string;            // Q4 — what makes them better/different
  filter: string;          // Q5 — services/terms to ignore
}

interface QuestionDef {
  key: keyof Power5;
  title: string;
  subtitle?: string;
  type: "textarea" | "input" | "chips";
  placeholder?: string;
  options?: ActionChoice[];
  optional?: boolean;
}

const QUESTIONS: QuestionDef[] = [
  {
    key: "priority",
    title: "If you could rank #1 on Google for just three things tomorrow, what would they be?",
    subtitle: "List up to three. The clearer, the sharper your strategy.",
    type: "textarea",
    placeholder: "e.g., your top services or products",
  },
  {
    key: "audience",
    title: "Who is your ideal customer, and where are they located?",
    type: "input",
    placeholder: "e.g., Homeowners in North Jersey",
  },
  {
    key: "action",
    title: "What is the #1 action you want a visitor to take?",
    subtitle: "Pick the conversion that matters most.",
    type: "chips",
    options: ["Call us", "Fill a lead form", "Buy a product", "Book a meeting"],
  },
  {
    key: "edge",
    title: "What makes you better or different than your competitors?",
    subtitle: "The honest answer — what you'd say to a friend.",
    type: "textarea",
    placeholder: "e.g., fast response, great reviews, years of experience",
  },
  {
    key: "filter",
    title: "Are there any services or search terms you want us to ignore?",
    subtitle: "Optional — anything off-limits or off-brand.",
    type: "input",
    placeholder: "e.g., commercial only — no residential, no DIY content",
    optional: true,
  },
];

const ACCENT = "#A855F7";
const ACCENT_2 = "#22D3EE";

const PositioningOnboarding = ({ clientId, recipientEmail, onComplete }: Props) => {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [animKey, setAnimKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [finalProgress, setFinalProgress] = useState(0);
  const [answers, setAnswers] = useState<Power5>({
    priority: "",
    audience: "",
    action: [],
    edge: "",
    filter: "",
  });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const total = QUESTIONS.length;
  const current = QUESTIONS[step];
  const value = answers[current.key];
  const canAdvance =
    current.optional ||
    (Array.isArray(value) ? value.length > 0 : typeof value === "string" && value.trim().length > 0);
  const isLast = step === total - 1;

  useEffect(() => {
    setAnimKey((k) => k + 1);
    // Autofocus on mount of each step (helps mobile keyboard pop up)
    const t = setTimeout(() => inputRef.current?.focus(), 380);
    return () => clearTimeout(t);
  }, [step]);

  const next = () => {
    if (!canAdvance) return;
    if (!isLast) {
      setDirection("forward");
      setStep(step + 1);
    } else {
      submit();
    }
  };

  const back = () => {
    if (step > 0) {
      setDirection("back");
      setStep(step - 1);
    }
  };

  const submit = async () => {
    if (!canAdvance) return;
    setSubmitting(true);
    setSubmitError(null);

    const start = Date.now();
    const duration = 3000;
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(95, (elapsed / duration) * 95);
      setFinalProgress(pct);
      if (elapsed >= duration) clearInterval(interval);
    }, 60);

    const hardTimeout = setTimeout(() => {
      clearInterval(interval);
      console.warn("[positioning] hard timeout reached — redirecting to dashboard");
      onComplete();
    }, 30000);

    // Map Power-5 answers onto the existing positioning schema so the
    // generate-positioning edge function keeps working without changes.
    const mapped = {
      seed_keywords: answers.priority,
      ideal_customer: answers.audience,
      target_market: answers.audience,
      signature_service: `Primary conversion goal: ${answers.action.length ? answers.action.join(", ") : "Not specified"}`,
      differentiator: answers.edge,
      credentials: "",
      years_in_business: "",
      use_cases: "",
      extra_notes: answers.filter
        ? `Topics / terms to AVOID: ${answers.filter}`
        : "",
    };

    try {
      const { data, error } = await supabase.functions.invoke("generate-positioning", {
        body: { client_id: clientId, answers: mapped },
      });
      if (error || !(data as any)?.success) {
        throw new Error(error?.message || (data as any)?.error || "Failed to generate positioning");
      }

      // NOTE: generate-first-articles is intentionally NOT invoked here.
      // trigger-first-run runs analyze-website → import-gsc-keywords →
      // score-keywords → generate-keywords → generate-first-articles
      // sequentially so business_type/location/website_url are populated
      // BEFORE article generation runs. Calling it here in parallel races
      // the pipeline and produces empty-context articles ("service null",
      // wrong language, generic photos, broken internal links).

      // Master ignition — fan out to all first-run engines (keywords,
      // competitors, page speed, backlinks, narrative, AI visibility,
      // directory profiles + submissions, score). Fire-and-forget.
      supabase.functions
        .invoke("trigger-first-run", { body: { client_id: clientId } })
        .then(({ error: tErr }) => {
          if (tErr) console.error("[positioning] trigger-first-run error:", tErr);
        });

      clearInterval(interval);
      clearTimeout(hardTimeout);
      setFinalProgress(100);

      toast({
        title: "✨ Calibration complete",
        description: "Every article we publish will reflect your strategy from now on.",
      });

      setTimeout(() => onComplete(), 600);
    } catch (err: any) {
      clearInterval(interval);
      clearTimeout(hardTimeout);
      console.error("[positioning] submit error:", err);
      setSubmitError(err?.message || "Something went wrong while calibrating your engine.");
      setFinalProgress(0);
      toast({
        title: "Something went wrong",
        description: err?.message || "We couldn't finalize your calibration.",
        variant: "destructive",
      });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && current.type === "input") {
      e.preventDefault();
      next();
    }
  };

  // ── FINAL SETUP STATE ──────────────────────────────────────────────
  if (submitting) {
    return (
      <div
        className="fixed inset-0 z-[200] flex flex-col items-center justify-center px-6 text-white"
        style={{
          background:
            "radial-gradient(ellipse at top, #2a0a4a 0%, #1a0533 35%, #0a0118 75%, #050010 100%)",
          fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${ACCENT}1A 0%, transparent 60%)`,
          }}
        />

        <div className="relative z-10 w-full max-w-[560px] text-center animate-[fade-in_0.6s_ease-out]">
          <div
            className="mx-auto mb-8 h-14 w-14 rounded-full"
            style={{
              background: submitError
                ? `radial-gradient(circle, #ff5b6e40 0%, transparent 70%)`
                : `radial-gradient(circle, ${ACCENT}55 0%, transparent 70%)`,
              boxShadow: submitError ? `0 0 40px #ff5b6e50` : `0 0 50px ${ACCENT}70`,
              animation: submitError ? "none" : "pulse 2s ease-in-out infinite",
            }}
          />
          {submitError ? (
            <>
              <h2 className="mb-3 text-[28px] font-[700] tracking-[-0.02em] md:text-[34px]">
                We hit a snag calibrating
              </h2>
              <p className="mb-10 text-[15px] leading-relaxed text-white/60 md:text-[16px]">
                {submitError} You can head to your dashboard now — we'll keep your answers safe.
              </p>
              <button
                onClick={() => onComplete()}
                className="rounded-full px-10 py-4 text-[15px] font-[600] text-white transition-all duration-300"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}, #D946EF)`,
                  boxShadow: `0 0 40px ${ACCENT}70`,
                }}
              >
                Go to dashboard →
              </button>
            </>
          ) : (
            <>
              <h2 className="mb-3 text-[28px] font-[700] tracking-[-0.02em] md:text-[36px]">
                Calibrating your OrbitRank engine…
              </h2>
              <p className="mb-10 text-[15px] leading-relaxed text-white/60 md:text-[16px]">
                Locking in your strategy, mapping your keywords, preparing your growth.
              </p>

              <div className="h-[6px] w-full overflow-hidden rounded-full bg-white/5">
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${finalProgress}%`,
                    background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_2})`,
                    boxShadow: `0 0 20px ${ACCENT}90`,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const progressPct = ((step + 1) / total) * 100;

  // ── QUESTION FLOW ──────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[200] overflow-hidden text-white"
      style={{
        height: "100dvh",
        background:
          "radial-gradient(ellipse at top, #2a0a4a 0%, #1a0533 35%, #0a0118 75%, #050010 100%)",
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 30%, ${ACCENT}14 0%, transparent 55%), radial-gradient(circle at 80% 90%, ${ACCENT_2}0F 0%, transparent 55%)`,
        }}
      />

      {/* Top progress bar (4px, neon gradient) */}
      <div className="absolute left-0 right-0 top-0 z-20 h-[4px] w-full bg-white/5">
        <div
          className="h-full transition-all duration-500 ease-out"
          style={{
            width: `${progressPct}%`,
            background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_2})`,
            boxShadow: `0 0 14px ${ACCENT}AA`,
          }}
        />
      </div>

      {/* Step indicator + back */}
      <div className="absolute left-0 right-0 top-0 z-10 px-6 pt-6 md:px-12 md:pt-8">
        <div className="mx-auto flex w-full max-w-[760px] items-center justify-between">
          <div className="text-[11px] font-[600] uppercase tracking-[0.18em] text-white/45">
            Step {step + 1} / {total}
          </div>
          {step > 0 ? (
            <button
              onClick={back}
              className="text-[12px] font-[500] text-white/50 transition-colors hover:text-white"
            >
              ← Back
            </button>
          ) : (
            <span className="text-[11px] font-[500] uppercase tracking-[0.18em] text-white/35">
              Calibration
            </span>
          )}
        </div>
      </div>

      {/* Question card */}
      <div className="absolute left-0 right-0 top-[60px] bottom-[110px] z-10 overflow-y-auto px-4 pb-6 md:top-[72px] md:bottom-[140px] md:px-12">
        <div className="mx-auto flex min-h-full w-full max-w-[760px] items-start justify-center md:items-center">
          <div
            key={animKey}
            className="w-full"
            style={{
              animation:
                direction === "forward"
                  ? "obSlideInRight 0.5s cubic-bezier(0.22, 1, 0.36, 1)"
                  : "obSlideInLeft 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {/* Glassmorphism card */}
            <div
              className="rounded-3xl border border-white/10 px-5 py-6 md:px-12 md:py-12"
              style={{
                background: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                boxShadow:
                  "0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            >
              <h2 className="mb-3 text-[20px] font-[700] leading-[1.2] tracking-[-0.02em] sm:text-[24px] md:text-[40px]">
                {current.title}
              </h2>
              {current.subtitle && (
                <p className="mb-5 text-[13px] text-white/55 md:mb-8 md:text-[16px]">
                  {current.subtitle}
                </p>
              )}
              {!current.subtitle && <div className="mb-5 md:mb-8" />}

              {current.type === "textarea" && (
                <textarea
                  ref={(el) => (inputRef.current = el)}
                  key={current.key}
                  value={value as string}
                  onChange={(e) => setAnswers({ ...answers, [current.key]: e.target.value })}
                  placeholder={current.placeholder}
                  rows={4}
                  autoFocus
                  className="w-full resize-none rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 text-[16px] text-white outline-none transition-all placeholder:text-white/25 md:text-[18px]"
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${ACCENT}, 0 0 28px ${ACCENT}40`;
                    e.currentTarget.style.borderColor = "transparent";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                  }}
                />
              )}

              {current.type === "input" && (
                <input
                  ref={(el) => (inputRef.current = el)}
                  key={current.key}
                  type="text"
                  value={value as string}
                  onChange={(e) => setAnswers({ ...answers, [current.key]: e.target.value })}
                  onKeyDown={onKeyDown}
                  placeholder={current.placeholder}
                  autoFocus
                  className="h-16 w-full rounded-2xl border border-white/10 bg-white/[0.04] px-5 text-[17px] text-white outline-none transition-all placeholder:text-white/25 md:text-[20px]"
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${ACCENT}, 0 0 28px ${ACCENT}40`;
                    e.currentTarget.style.borderColor = "transparent";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                  }}
                />
              )}

              {current.type === "chips" && current.options && (
                <div className="flex flex-wrap gap-3">
                  <p className="w-full mb-1 text-[12px] text-white/45">Select all that apply</p>
                  {current.options.map((opt) => {
                    const selected = Array.isArray(value) && (value as ActionChoice[]).includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const curr = Array.isArray(answers.action) ? answers.action : [];
                          const nextArr = curr.includes(opt)
                            ? curr.filter((x) => x !== opt)
                            : [...curr, opt];
                          setAnswers({ ...answers, action: nextArr });
                        }}
                        className="rounded-full px-5 py-3 text-[14px] font-[600] transition-all duration-200 md:text-[15px]"
                        style={{
                          background: selected
                            ? `linear-gradient(135deg, ${ACCENT}, #D946EF)`
                            : "rgba(255,255,255,0.04)",
                          color: selected ? "#fff" : "rgba(255,255,255,0.75)",
                          border: selected
                            ? "1px solid transparent"
                            : "1px solid rgba(255,255,255,0.12)",
                          boxShadow: selected
                            ? `0 0 28px ${ACCENT}80, 0 4px 14px ${ACCENT}40`
                            : "none",
                          transform: selected ? "translateY(-1px)" : "none",
                        }}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed bottom CTA bar (mobile-first thumb access) */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 px-5 pb-[max(env(safe-area-inset-bottom),16px)] pt-4 md:px-12 md:pt-6"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,1,24,0) 0%, rgba(10,1,24,0.85) 40%, rgba(10,1,24,1) 100%)",
        }}
      >
        <div className="mx-auto flex w-full max-w-[760px] flex-col items-center gap-2">
          <button
            onClick={next}
            disabled={!canAdvance}
            className="group relative h-14 w-full overflow-hidden rounded-full text-[15px] font-[700] tracking-[0.01em] text-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-40 md:h-16 md:w-auto md:px-12 md:text-[16px]"
            style={{
              background: canAdvance
                ? `linear-gradient(135deg, ${ACCENT}, #D946EF)`
                : "rgba(255,255,255,0.06)",
              boxShadow: canAdvance ? `0 0 40px ${ACCENT}70, 0 8px 24px ${ACCENT}30` : "none",
            }}
            onMouseEnter={(e) => {
              if (canAdvance) {
                e.currentTarget.style.boxShadow = `0 0 60px ${ACCENT}AA, 0 10px 30px ${ACCENT}55`;
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = canAdvance
                ? `0 0 40px ${ACCENT}70, 0 8px 24px ${ACCENT}30`
                : "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {isLast ? "Finish calibration →" : "Next →"}
          </button>
          <div className="hidden text-[11px] text-white/30 md:block">
            Press Enter to continue
          </div>
        </div>
      </div>

      <style>{`
        @keyframes obSlideInRight {
          0% { opacity: 0; transform: translateX(40px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes obSlideInLeft {
          0% { opacity: 0; transform: translateX(-40px); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default PositioningOnboarding;
